import React, { useState, useEffect, useRef } from "react";
import ReactFlow, { Background, Controls, MiniMap } from "reactflow";
import "reactflow/dist/style.css";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, Fab, Icon, Snackbar, TextField } from "@mui/material";
import AddJobDialog from "components/AddJobDialog";
import MDBox from "components/MDBox";
import { addDepartment } from "services/api";
import DepartmentModel from "models/departmentModel";
import { addJob } from "services/api";
import JobModel from "models/jobModel";
import { deleteJob } from "services/api";
import { updateJobBranches } from "services/api";
import { getJobToBranch } from "services/api";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useAuth } from "context/authContext";
import { getCompanies, updateCompanyData } from "services/api";
import { debounce } from "lodash"; // Import debounce from lodash
import { Card, CardContent, Typography, Grid, Paper } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { Autocomplete } from "@mui/material";
import dayjs from "dayjs"; // Import dayjs for date formatting
import {
  getTopDashboardLayer,
  getInitiativesByCompanyId,
  deleteInitiativeById,
  addInitiative,
  updateInitiative,
} from "services/api";
import AddInitiativeDialog from "components/AddInitiativeDialog";
import { getJobs } from "services/api";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import AddFloatingButton from "components/AddFloatingButton";
import { useTranslation } from "react-i18next";
function InitiativesView({ canEdit }) {
  const { t } = useTranslation();
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const { employee } = useAuth();
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  // const [jobNames, setJobNames] = useState(null);
  const [jobNames, setJobNames] = useState([]);

  // State for Add Initiative Dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  // State for Edit Initiative Dialog
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editedInitiative, setEditedInitiative] = useState(null);

  const [confOpen, setConfOpen] = useState(false);

  const formatDateForInput = (date) => {
    return date ? dayjs(date, ["YYYY/MM/DD", "MM/DD/YYYY"]).format("YYYY-MM-DD") : "";
  };

  const formatDateForStorage = (date) => {
    return date ? dayjs(date, "YYYY-MM-DD").format("YYYY/MM/DD") : "";
  };

  const handleAddInitiative = async (newInitiative) => {
    try {
      await addInitiative(newInitiative); // ✅ Call API
      const companyId = employee.companyId;
      const initiativesData = await getInitiativesByCompanyId(companyId);
      setInitiatives(initiativesData);
      setOpenAddDialog(false); // ✅ Close dialog after adding
    } catch (error) {
      console.error("Error adding initiative:", error);
    }
  };

  const handleOpenAddDialog = () => {
    setSelectedInitiative(null);
    // setOpenEditDialog(false); // Ensure edit
    setOpenAddDialog(true); // UPDATE: Only opens AddInitiativeDialog
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedInitiative(null);
  };

  // Open Edit Initiative Dialog
  const handleOpenEditDialog = (initiative) => {
    if (!canEdit) return;
    // setOpenAddDialog(false);  // Close add dialog before opening edit //NEW
    setEditedInitiative({ ...initiative }); // Copy the initiative data
    setSelectedInitiative(initiative);

    setOpenAddDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenAddDialog(false);
    setEditedInitiative(null);
    setSelectedInitiative(null);
  };

  const handleDeleteInitiative = async () => {
    if (!selectedInitiative) return;
    setConfOpen(true);
  };

  // Handle input change in the dialog
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedInitiative((prev) => ({ ...prev, [name]: value }));
  };

  const handleJobChange = (event, newValue) => {
    setEditedInitiative((prev) => ({
      ...prev,
      jobId: newValue ? newValue.jobId : "",
    }));
  };

  const handleSaveChanges = async () => {
    if (!editedInitiative) return;

    try {
      const updatedInitiative = await updateInitiative(editedInitiative);

      if (updatedInitiative !== "error") {
        setInitiatives((prevInitiatives) =>
          prevInitiatives.map((initiative) =>
            initiative.initiativeId === updatedInitiative.initiativeId
              ? updatedInitiative
              : initiative
          )
        );
        setOpenAddDialog(false); // Close the dialog after successful update
      }
    } catch (error) {
      console.error("Error saving initiative changes:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const companyId = employee.companyId;
      const initiativesData = await getInitiativesByCompanyId(companyId);
      setInitiatives(initiativesData);
      const jobsData = await getJobs();
      setJobNames(jobsData);
    };
    fetchData().then((e) => {
      setLoading(false);
    });
  }, []);

  return (
    <MDBox p={3}>
      <MDTypography variant="h4" mb={3} textAlign="center">
        {t("admin.strategicInfo.initiatives.title")}
      </MDTypography>

      {canEdit && <AddFloatingButton onClick={handleOpenAddDialog} />}

      <Grid container spacing={2}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={1}>
            <CircularProgress size={20} />
          </Box>
        ) : initiatives.length === 0 ? (
          <Typography variant="h6" textAlign="center">
            {t("admin.strategicInfo.initiatives.noData")}
          </Typography>
        ) : (
          initiatives?.map((initiative) => (
            <Grid item key={initiative.initiativeId} xs={12} sm={6} md={4} lg={3}>
              <Paper
                elevation={2}
                sx={{
                  padding: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  transition: "0.3s",
                }}
                // onClick={() => handleOpenDialog(initiative)} // 👈 Opens dialog with employee data
                // UPDATE: Use handleEditClick instead of handleOpenDialog to prefill data
                // onClick={() => handleEditClick(initiative)} // 👈 Opens dialog with prefilled initiative data
                onClick={() => handleOpenEditDialog(initiative)} // UPDATE: Opens Edit dialog only
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    lineHeight: "1.2em",
                    maxHeight: "2.4em",
                    minHeight: "2.4em",
                    wordBreak: "break-word",
                  }}
                >
                  {initiative.initiativeName}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {t("admin.strategicInfo.initiatives.type")}: {initiative.initiativeType}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.strategicInfo.initiatives.completion")}:
                  {initiative.completionPercentage}%
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {t("admin.strategicInfo.initiatives.jobTitle")}:{" "}
                  {jobNames.find((job) => job.jobId === initiative?.jobId)?.jobName || "N/A"}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {t("admin.strategicInfo.initiatives.startDate")}:{" "}
                  {formatDateForInput(initiative.startDate)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.strategicInfo.initiatives.endDate")}:{" "}
                  {formatDateForInput(initiative.endDate)}
                </Typography>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>

      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} fullWidth>
        <DialogTitle>{t("admin.strategicInfo.initiatives.editDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("admin.strategicInfo.initiatives.dialog.nameLabel")}
            name={t("admin.strategicInfo.initiatives.dialog.nameLabel")}
            fullWidth
            margin="normal"
            value={editedInitiative?.initiativeName || ""}
            onChange={handleInputChange}
          />
          <TextField
            label={t("admin.strategicInfo.initiatives.dialog.typeLabel")}
            name={t("admin.strategicInfo.initiatives.dialog.typeLabel")}
            fullWidth
            margin="normal"
            value={editedInitiative?.initiativeType || ""}
            onChange={handleInputChange}
          />
          <TextField
            label={t("admin.strategicInfo.initiatives.dialog.completionLabel")}
            name={t("admin.strategicInfo.initiatives.dialog.completionLabel")}
            type="number"
            fullWidth
            margin="normal"
            value={editedInitiative?.completionPercentage || ""}
            onChange={handleInputChange}
          />

          <FormControl fullWidth margin="normal">
            <Autocomplete
              options={jobNames || []}
              getOptionLabel={(job) => job.jobName} // Display job name in dropdown
              value={jobNames.find((job) => job.jobId === editedInitiative?.jobId) || null}
              onChange={handleJobChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.strategicInfo.initiatives.dialog.jobTitleLabel")}
                />
              )}
            />
          </FormControl>

          <TextField
            label={t("admin.strategicInfo.initiatives.dialog.startDateLabel")}
            name={t("admin.strategicInfo.initiatives.dialog.startDateLabel")}
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={formatDateForInput(editedInitiative?.startDate)} // ✅ Format for input
            onChange={(e) =>
              handleInputChange({
                target: { name: "startDate", value: formatDateForStorage(e.target.value) }, // ✅ Convert for storage
              })
            }
          />
          <TextField
            label={t("admin.strategicInfo.initiatives.dialog.endDateLabel")}
            name={t("admin.strategicInfo.initiatives.dialog.endDateLabel")}
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={editedInitiative?.endDate || ""}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={() => setOpenDialog(false)} variant="contained" color="error"> */}
          <Button onClick={() => setOpenEditDialog(false)} color="secondary">
            {t("admin.strategicInfo.initiatives.dialog.cancel")}
          </Button>
          <Button
            onClick={handleDeleteInitiative}
            color="error"
            sx={{
              color: "red",
              "&:hover": { backgroundColor: "red", color: "#fff" },
            }}
          >
            {t("admin.strategicInfo.initiatives.dialog.delete")}
          </Button>

          <Button
            onClick={handleSaveChanges}
            color="primary"
            sx={{
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {t("admin.strategicInfo.initiatives.dialog.update")}
          </Button>
        </DialogActions>
      </Dialog>

      <AddInitiativeDialog
        open={openAddDialog} // UPDATE: Uses separate state
        // open={openInitiativeDialog}
        onClose={handleCloseAddDialog} // UPDATE: Uses separate close function
        // onClose={handleCloseInitiativeDialog}
        initiative={selectedInitiative}
        // companies={companies} // Pass a list of companies if needed
        handleDelete={handleDeleteInitiative}
        handleSubmit={handleAddInitiative}
        companyId={employee.companyId}
        jobs={jobNames}
      />

      <ConfirmationDialog
        open={confOpen}
        onClose={() => {
          setConfOpen(false);
        }}
        onConfirm={async () => {
          try {
            await deleteInitiativeById(selectedInitiative.initiativeId);

            // Remove the deleted initiative from the UI
            setInitiatives((prev) =>
              prev.filter(
                (initiative) => initiative.initiativeId !== selectedInitiative.initiativeId
              )
            );

            setOpenAddDialog(false); // Close Edit dialog
          } catch (error) {
            console.error("Error deleting initiative:", error);
          }
          setConfOpen(false);
        }}
      />
    </MDBox>
  );
}

InitiativesView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default InitiativesView;
